import { ChipCondition } from '@/types/gql-op-types';

/**
 * Canonical wording for every chip condition, shared between every surface that
 * shows one (StatusBadge, removal-reason dropdowns, toasts). Keep all condition
 * copy here so the three surfaces don't drift out of sync.
 *
 *  - `selectLabel` — full-sentence label for the "why are you removing this
 *    chip" dropdown in ChangeConditionModal / RemoveChipModal. WORKING is
 *    empty because it's never a removal reason.
 *  - `badgeLabel` — short noun used in the Condition StatusBadge.
 */
interface ChipConditionInfo {
  value: ChipCondition;
  selectLabel: string;
  badgeLabel: string;
}

const CHIP_CONDITION_INFO: Record<ChipCondition, ChipConditionInfo> = {
  [ChipCondition.WORKING]: {
    value: ChipCondition.WORKING,
    selectLabel: '',
    badgeLabel: 'Working',
  },
  [ChipCondition.DAMAGED]: {
    value: ChipCondition.DAMAGED,
    selectLabel: 'Chip is damaged',
    badgeLabel: 'Damaged',
  },
  [ChipCondition.DECEASED_PET]: {
    value: ChipCondition.DECEASED_PET,
    selectLabel: 'Pet has passed',
    badgeLabel: 'Pet has passed',
  },
  [ChipCondition.MISSING]: {
    value: ChipCondition.MISSING,
    selectLabel: 'I never received this chip',
    badgeLabel: 'Never received',
  },
};

/**
 * Options for the "why are you removing this chip" select field. Only the
 * non-WORKING conditions can be chosen — marking a chip as working is not a
 * supported user flow.
 */
export const CHIP_REMOVAL_REASON_OPTIONS: readonly { value: ChipCondition; label: string }[] = [
  ChipCondition.DAMAGED,
  ChipCondition.DECEASED_PET,
  ChipCondition.MISSING,
].map((value) => ({ value, label: CHIP_CONDITION_INFO[value].selectLabel }));

export function chipConditionBadgeLabel(condition: ChipCondition): string {
  return CHIP_CONDITION_INFO[condition]?.badgeLabel ?? condition;
}

export function chipConditionSelectLabel(condition: ChipCondition): string {
  return CHIP_CONDITION_INFO[condition]?.selectLabel ?? condition;
}
